/* eslint-disable @typescript-eslint/camelcase */
import React from 'react';

import { Grid, Typography, makeStyles } from '@material-ui/core';
import i18n from 'i18n';
import {
  PeriodCountAccess,
  DateFilterFields,
} from 'modules/environments/interfaces';
import ViewIcon from '@material-ui/icons/Visibility';
import FiberManualRecordIcon from '@material-ui/icons/FiberManualRecord';
import { RolesEnum } from 'modules/authenticate/interfaces';
import ApplicationState from 'store/interfaces';
import { connect } from 'react-redux';
import { modalClose, modalOpen } from 'modules/modal/actions';
import {
  getDashboardSessionsGraphRequest,
  setPeriodFilter,
} from 'modules/environments/actions';
import { ModalFields } from 'modules/modal/interfaces';
import FilterSelector from 'components/FilterSelector';
import { filterPeriodOptions } from 'utils/graphs';
import { isClientOperator } from 'utils/role';
import { useParams } from 'react-router';
import Button from 'components/Button';
import EnvironmentGraphCard from '../EnvironmentGraphCard';
import styles, { overrideModalGraph } from './styles';
import SessionHistoryGraph from './SessionHistoryGraph';
import SessionHistoryModal from './SessionHistoryModal';

interface SessionHistoryGraphCardProps {
  role?: RolesEnum;
  periodFilter: PeriodCountAccess;
  modalOpen: (props: ModalFields) => void;
  modalClose: () => void;
  setPeriodFilter: (prop: string) => void;
  getDashboardSessionsGraphRequest: ({ id, query }) => void;
}

const SessionHistoryGraphCard = ({
  role,
  periodFilter,
  modalOpen,
  modalClose,
  setPeriodFilter,
  getDashboardSessionsGraphRequest,
}: SessionHistoryGraphCardProps) => {
  const classes = makeStyles(styles())();
  const { id } = useParams<{
    id: string;
  }>();

  const [dates, setDates] = React.useState<DateFilterFields>({
    'start-date': '',
    'end-date': '',
  });

  React.useEffect(() => {
    const query =
      dates?.['start-date'] && dates?.['end-date']
        ? dates
        : { period: periodFilter };

    getDashboardSessionsGraphRequest({
      id,
      query,
    });
  }, [getDashboardSessionsGraphRequest, id, periodFilter, dates]);

  const handleOpenGraphModal = () => {
    modalOpen({
      overrideClasses: overrideModalGraph,
      maxWidth: 'xl',
      title: i18n.t('ENVIRONMENT_DASHBOARD.ENVIRONMENT_USE_KEY'),
      content: <SessionHistoryModal dates={dates} setDates={setDates} />,
      genericFooter: {
        action: () => {
          setDates({ 'start-date': '', 'end-date': '' });
          modalClose();
        },
        label: i18n.t('CLOSE_KEY'),
      },
    });
  };

  return (
    <Grid item className={classes.sessionHistoryContainer}>
      <Grid container className={classes.cardHeader}>
        <FilterSelector
          options={filterPeriodOptions(setPeriodFilter)}
          currentValue={{
            label: i18n.t(
              `ENVIRONMENT_DASHBOARD.LAST_${periodFilter.toUpperCase()}`
            ),
            onClick: () => null,
          }}
        />
      </Grid>
      <EnvironmentGraphCard>
        <Grid item xs={12} onClick={() => handleOpenGraphModal()}>
          <SessionHistoryGraph />
        </Grid>
        <Grid item xs={9} className={classes.graphLegendsContainer}>
          <Button
            startIcon={<ViewIcon />}
            variant="outlined"
            onClick={() => handleOpenGraphModal()}
            className={classes.detailsButton}
          >
            {i18n.t('SEE_DETAILS_KEY')}
          </Button>
          <Grid item>
            <Typography className={classes.graphLegendItem}>
              <FiberManualRecordIcon className={classes.graphSessions} />
              {i18n.t('ENVIRONMENT_DASHBOARD.SESSIONS_MAX_KEY')}
            </Typography>

            {!isClientOperator(role) && (
              <Typography className={classes.graphLegendItem}>
                <FiberManualRecordIcon className={classes.graphVacancies} />
                {i18n.t('ENVIRONMENT_DASHBOARD.VACANCIES_MAX_KEY')}
              </Typography>
            )}
          </Grid>
        </Grid>
      </EnvironmentGraphCard>
    </Grid>
  );
};

const mapStateToProps = ({
  environments: { periodFilter },
  authenticate: { data },
}: ApplicationState) => ({
  role: data?.role,
  periodFilter,
});

export default connect(mapStateToProps, {
  getDashboardSessionsGraphRequest,
  modalOpen,
  modalClose,
  setPeriodFilter,
})(SessionHistoryGraphCard);
